import { useCallback, useEffect, useRef, useState } from 'react';
import styles from '../ProjectEditForm.module.css';
import EmailAuthCreateUser from '../../../components/EmailAuthCreateUser';

export default function CreateParticipantAccountDialog({
    open,
    onClose,
    folderId,
    onAddRole,
    disabled = false,
}) {
    const dialogRef = useRef(null);
    const [resolvedUser, setResolvedUser] = useState(null);
    const [addPending, setAddPending] = useState(false);
    const [addError, setAddError] = useState('');
    const [addSuccess, setAddSuccess] = useState('');

    const close = useCallback(() => {
        setResolvedUser(null);
        setAddPending(false);
        setAddError('');
        setAddSuccess('');
        onClose?.();
    }, [onClose]);

    const addAsAnnotator = useCallback(async (user) => {
        if (!user?._id || !folderId || disabled) return;
        setAddPending(true);
        setAddError('');
        setAddSuccess('');
        try {
            const ok = await onAddRole(user._id, 'annotator');
            if (ok === false) {
                setAddError('Could not add user as annotator. Try again.');
                return;
            }
            setAddSuccess('Added as annotator.');
        } catch {
            setAddError('Could not add user as annotator. Try again.');
        } finally {
            setAddPending(false);
        }
    }, [folderId, disabled, onAddRole]);

    useEffect(() => {
        if (!open) return undefined;
        function onKeyDown(e) {
            if (e.key === 'Escape') {
                e.preventDefault();
                close();
            }
        }
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [open, close]);

    useEffect(() => {
        if (!open) return undefined;
        const id = requestAnimationFrame(() => {
            const root = dialogRef.current;
            const first = root?.querySelector('input, button:not([disabled])');
            first?.focus();
        });
        return () => cancelAnimationFrame(id);
    }, [open]);

    function handleBackdropMouseDown(e) {
        if (e.target === e.currentTarget) {
            close();
        }
    }

    if (!open) return null;

    return (
        <div
            className={styles.confirmOverlay}
            role="presentation"
            onMouseDown={handleBackdropMouseDown}
        >
            <div
                ref={dialogRef}
                className={`${styles.confirmDialog} ${styles.confirmDialogJoinCodes}`}
                role="dialog"
                aria-modal="true"
                aria-labelledby="create-participant-modal-title"
                onMouseDown={(e) => e.stopPropagation()}
            >
                <h2 id="create-participant-modal-title" className={styles.confirmDialogTitle}>
                    Create participant account
                </h2>

                <EmailAuthCreateUser
                    onUserResolved={(u) => {
                        setResolvedUser(u);
                        setAddPending(false);
                        setAddError('');
                        setAddSuccess('');
                        addAsAnnotator(u);
                    }}
                />

                <div style={{ marginTop: 14 }}>
                    {addPending ? (
                        <p className={styles.emptyState} aria-live="polite">
                            Adding as annotator…
                        </p>
                    ) : null}
                    {addError ? (
                        <>
                            <p className={styles.itemActionError} role="alert" style={{ marginTop: 10 }}>
                                {addError}
                            </p>
                            <button
                                type="button"
                                className={styles.buttonPrimary}
                                onClick={() => addAsAnnotator(resolvedUser)}
                                disabled={disabled || addPending || !resolvedUser?._id}
                                style={{ marginTop: 10 }}
                            >
                                Retry add as annotator
                            </button>
                        </>
                    ) : null}
                    {addSuccess ? (
                        <p className={styles.emptyState} aria-live="polite" style={{ marginTop: 10 }}>
                            {addSuccess}
                        </p>
                    ) : null}
                </div>

                <div className={styles.confirmActions}>
                    <button
                        type="button"
                        className={styles.buttonSecondary}
                        onClick={close}
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
}
